import { useCapabilities } from '@/api/hooks';
import { EmptyState, Skeleton } from '@/components/ui';

/**
 * A surface that opens when the backend says its capability is implemented.
 *
 * The per-tool analysis surface was substituted at build time: `DEMO ? real :
 * <GatedSurface/>` in the route table. So the day the backend closes the
 * reconstruction gap, this product would still say "unavailable" until someone
 * rebuilt and redeployed the frontend. Every other surface reads its gate at
 * runtime, and this one now does too.
 *
 * While the capability read is in flight the answer is unknown, and unknown is
 * drawn as waiting rather than as unavailable: telling an operator a feature
 * does not exist because a request has not come back yet is the same class of
 * error as reporting an unread count as zero.
 */
export function GatedByCapability({
  capability,
  title,
  children,
}: {
  /** The key in `capabilities`, e.g. `reconstruction`. */
  capability: string;
  /** What the operator was trying to open. */
  title: string;
  children: React.ReactNode;
}) {
  const caps = useCapabilities();
  const state = (caps.data?.capabilities as Record<string, string> | undefined)?.[capability];

  if (caps.isPending) return <Skeleton className="m-5 h-24" />;

  if (caps.isError) {
    return (
      <EmptyState
        title={`${title} could not be checked`}
        description="The capability list did not come back, so whether this is available is unknown. It is not being reported as missing on the strength of a failed request."
      />
    );
  }

  if (state !== 'IMPLEMENTED') {
    return (
      <EmptyState
        title={`${title} is not available yet`}
        description="The backend reports this capability as unbuilt. This page opens on its own the moment that changes; no new release of this app is needed."
      />
    );
  }

  return <>{children}</>;
}
